import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Gift, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Newsletter Popup Component with Exit Intent Detection
// Settings are loaded from backend (admin configurable)
export const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [triggerType, setTriggerType] = useState('timed'); // 'timed' or 'exit_intent'
  
  // Dynamic settings from backend
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch popup settings from backend (with cache-busting)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Add timestamp to bust browser cache
        const timestamp = Date.now();
        const res = await axios.get(`${API_URL}/api/marketing/popup-settings/public?_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        setSettings(res.data);
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch popup settings:', error);
        // Use defaults if fetch fails - longer delay for better UX
        setSettings({
          timed_popup: {
            enabled: true,
            delay_seconds: 30,
            headline: "Get 10% Off Your First Order!",
            body: "Subscribe to our newsletter and receive exclusive deals.",
            cta_text: "Subscribe Now",
            offer_code: "WELCOME10"
          },
          exit_intent_popup: {
            enabled: true,
            headline: "Wait! Don't Leave Empty-Handed",
            body: "Sign up for our newsletter and get exclusive discounts on your first order.",
            cta_text: "Get My Discount",
            offer_code: "SAVE10"
          }
        });
        setSettingsLoaded(true);
      }
    };
    
    fetchSettings();
  }, []);

  // Get current popup config based on trigger type
  const getCurrentConfig = useCallback(() => {
    if (!settings) return null;
    return triggerType === 'exit_intent' ? settings.exit_intent_popup : settings.timed_popup;
  }, [settings, triggerType]);

  // Track popup impression
  const trackImpression = useCallback(async (type) => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=impression&trigger_type=${type}`);
    } catch (e) {
      // Silent fail
    }
  }, []);

  // Track popup conversion
  const trackConversion = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=conversion&trigger_type=${triggerType}`);
    } catch (e) {
      // Silent fail
    }
  }, [triggerType]);

  // Exit intent detection
  const handleMouseLeave = useCallback((e) => {
    if (!settings?.exit_intent_popup?.enabled) return;
    
    if (e.clientY <= 0 && !triggered) {
      const dismissed = localStorage.getItem('newsletter_dismissed');
      const alreadySubscribed = localStorage.getItem('newsletter_subscribed');
      
      if (!dismissed && !alreadySubscribed) {
        setTriggerType('exit_intent');
        setIsOpen(true);
        setTriggered(true);
        trackImpression('exit_intent');
      }
    }
  }, [triggered, trackImpression, settings]);

  // Setup popup triggers based on settings
  useEffect(() => {
    if (!settingsLoaded || !settings) return;
    
    // Don't show popup on admin pages or cart/checkout pages
    if (window.location.pathname.startsWith('/admin') || 
        window.location.pathname.startsWith('/cart') ||
        window.location.pathname.startsWith('/checkout')) {
      return;
    }
    
    const dismissed = localStorage.getItem('newsletter_dismissed');
    const alreadySubscribed = localStorage.getItem('newsletter_subscribed');
    const sessionShown = sessionStorage.getItem('newsletter_shown_this_session');
    
    // Don't show if already dismissed, subscribed, or shown this session
    if (dismissed || alreadySubscribed || sessionShown) {
      return;
    }

    // Exit intent (only on desktop)
    if (settings.exit_intent_popup?.enabled) {
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    // Timed popup - use longer delay (30 seconds default instead of 5)
    let timer;
    if (settings.timed_popup?.enabled) {
      const delayMs = Math.max((settings.timed_popup.delay_seconds || 30) * 1000, 15000); // Minimum 15 seconds
      timer = setTimeout(() => {
        if (!triggered) {
          setTriggerType('timed');
          setIsOpen(true);
          setTriggered(true);
          sessionStorage.setItem('newsletter_shown_this_session', 'true');
          trackImpression('timed');
        }
      }, delayMs);
    }

    // Mobile scroll trigger (70% scroll instead of 50%)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!triggered && window.innerWidth < 768 && settings.timed_popup?.enabled) {
          const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
          if (scrollPercentage > 70) {
            setTriggerType('timed');
            setIsOpen(true);
            setTriggered(true);
            sessionStorage.setItem('newsletter_shown_this_session', 'true');
            trackImpression('timed');
          }
        }
      }, 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (timer) clearTimeout(timer);
      clearTimeout(scrollTimeout);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [settingsLoaded, settings, handleMouseLeave, triggered, trackImpression]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('newsletter_dismissed', 'true');
    sessionStorage.setItem('newsletter_shown_this_session', 'true');
  };

  // Handle clicking outside the popup to close it
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle Escape key to close popup
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const config = getCurrentConfig();
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { 
        email, 
        name,
        interests: ['new-products', 'sales', 'tips'],
        offer_code: config?.offer_code || '',
        trigger_type: triggerType
      });
      setSubscribed(true);
      localStorage.setItem('newsletter_subscribed', 'true');
      trackConversion();
      setTimeout(() => setIsOpen(false), 3000);
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current popup configuration
  const config = getCurrentConfig();

  if (!isOpen || !config) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" 
      data-testid="newsletter-popup"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-1 hover:bg-white/10 rounded-full transition-colors"
          data-testid="newsletter-popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        {subscribed ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0a0203] mb-2">
              You're In!
            </h3>
            <p className="text-gray-600 mb-4">
              Thanks for subscribing to HomeLyph updates.
            </p>
            {config.offer_code && (
              <div className="bg-[#fdc31c]/10 border-2 border-dashed border-[#fdc31c] rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-2">Your exclusive code:</p>
                <p className="text-2xl font-bold font-mono text-[#003456]">{config.offer_code}</p>
                <p className="text-xs text-gray-500 mt-2">Use at checkout for your discount</p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Header Section */}
            <div 
              className="p-6 text-white text-center"
              style={{ backgroundColor: triggerType === 'exit_intent' ? '#dc2626' : '#003456' }}
            >
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                {triggerType === 'exit_intent' ? (
                  <Gift className="h-7 w-7" />
                ) : (
                  <Mail className="h-7 w-7" />
                )}
              </div>
              <h3 className="text-xl font-bold mb-2">
                {config.headline}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {config.body}
              </p>
            </div>

            {/* Offer Code Display */}
            {config.offer_code && (
              <div className="px-6 -mt-4">
                <div className="bg-[#fdc31c] rounded-xl p-4 text-center shadow-lg">
                  <p className="text-xs font-medium text-[#0a0203]/70 mb-1">USE CODE AT CHECKOUT</p>
                  <p className="text-2xl font-bold font-mono text-[#0a0203]">{config.offer_code}</p>
                </div>
              </div>
            )}

            {/* Form Section */}
            <div className="p-6 pt-4">
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full"
                  data-testid="newsletter-popup-name"
                />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full"
                  data-testid="newsletter-popup-email"
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full text-white py-3 font-semibold"
                  style={{ backgroundColor: triggerType === 'exit_intent' ? '#dc2626' : '#003456' }}
                  data-testid="newsletter-popup-submit"
                >
                  {loading ? 'Subscribing...' : config.cta_text}
                </Button>
              </form>

              <p className="text-xs text-gray-400 mt-4 text-center">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </>
        )}
        
        {/* Trigger Type Indicator (hidden, for debugging) */}
        <div className="hidden" data-testid="popup-trigger-type" data-trigger={triggerType} />
      </div>
    </div>
  );
};

// Newsletter Footer Form Component
export const NewsletterFooter = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      localStorage.setItem('newsletter_subscribed', 'true');
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="flex items-center gap-2 text-[#fdc31c]">
        <CheckCircle className="h-5 w-5" />
        <span>Thanks for subscribing!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
          required
          data-testid="newsletter-footer-email"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="bg-[#003456] hover:bg-[#001d30] text-white"
        data-testid="newsletter-footer-submit"
      >
        {loading ? '...' : 'Subscribe'}
      </Button>
    </form>
  );
};

export default NewsletterPopup;
